import Select from 'antd/es/select';
import { useEffect, useMemo } from 'react';

import { useGetDisbursementProductSchemas } from 'api';

import { useOrganisationId } from 'hooks/context';

import { DATA_REQUIRED_SELECTOR_TEST_ID } from '../constants';

type Props = {
  onChange: (value: string) => void;
  onError?: (error: any, fields: string[]) => void;
  name?: string;
  isActive?: boolean;
  value: string;
  corridorId: string;
  errorMessage?: string;
};

export const ProductSchemaSelector = ({
  onChange,
  value,
  corridorId,
  isActive,
  errorMessage
}: Props) => {
  const organisationId = useOrganisationId<number>();

  const productId = useMemo(() => {
    return corridorId == null || corridorId === ''
      ? undefined
      : Number(corridorId);
  }, [corridorId]);

  const {
    data: schemaOptions = [],
    isFetching: isSchemasPending,
    isError
  } = useGetDisbursementProductSchemas(
    organisationId,
    { product_id: productId as number },
    {
      query: {
        enabled: Boolean(productId),
        select(res) {
          return (
            res.data?.map((schema) => ({
              value: String(schema.id),
              label: schema.label
            })) ?? []
          );
        }
      }
    }
  );

  const schemaLoadError = isError
    ? 'Something went wrong. Please try another product or contact support.'
    : null;

  useEffect(() => {
    if (schemaOptions.length === 1) {
      onChange(schemaOptions[0].value);
    }
  }, [schemaOptions, onChange]);

  const isDisabled =
    isActive ||
    isSchemasPending ||
    isError ||
    schemaOptions.length === 0 ||
    schemaOptions.length === 1;

  return (
    <>
      <label className="mb-0.5 pl-2 text-xs">Data required</label>

      <Select
        data-testid={DATA_REQUIRED_SELECTOR_TEST_ID}
        options={schemaOptions}
        onChange={onChange}
        disabled={isDisabled}
        value={value}
        loading={isSchemasPending}
        status={
          errorMessage && !!corridorId.length && isActive ? 'error' : undefined
        }
      />

      {isError && (
        <div className="ml-2 mt-4 text-xs text-red-500">{schemaLoadError}</div>
      )}
    </>
  );
};
